import { cn } from "@u/cn";
import { Eye, EyeOff, X, type LucideIcon } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

// 1. Extendemos las props para incluir el icono opcional.
//    `size` sombrea el atributo nativo (ancho en chars, casi nunca usado) por
//    la densidad del control — mismo patrón que Select/Switch.
interface InputProps extends Omit<React.ComponentProps<"input">, "size"> {
  icon?: LucideIcon;
  onClear?: () => void;
  rightAction?: React.ReactNode;
  /** Densidad: `default` (auth/marketing) vs `sm` (dashboard). */
  size?: "sm" | "default";
  /** Renderiza SOLO el `<input>` (sin el div wrapper) para filas flex/inline o
   *  contenedores propios. Se ignora si hay icon/onClear/rightAction (esas
   *  decoraciones necesitan el wrapper). */
  bare?: boolean;
}

function Input({
  className,
  type,
  icon: Icon,
  onClear,
  rightAction,
  value,
  size = "default",
  bare = false,
  ...props
}: InputProps) {
  const { t } = useTranslation();
  const hasValue = value !== undefined && value !== null && value !== "";
  const sm = size === "sm";
  const decorated = Boolean(Icon || onClear || rightAction);

  const handleClear = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (onClear) onClear();
    },
    [onClear],
  );

  const inputEl = (
    <input
      type={type}
      value={value}
      className={cn(
        // Estructura base compartida
        // Sans normal, como Textarea: el mono uppercase es de los labels
        // (FieldLabel); si el valor usa la misma letra no se distingue del label.
        "w-full outline-none transition-all border duration-300",
        // Colores base unificados
        "bg-background border-input text-foreground placeholder:text-muted-foreground",
        // Foco semántico
        "focus:border-ring focus:bg-background focus:ring-4 focus:ring-ring/10",
        // Estados
        "disabled:opacity-50 disabled:cursor-not-allowed",
        // Densidad
        // text-base en mobile (16px, evita zoom iOS) → text-sm en desktop
        sm
          ? "py-1.5 rounded-xl text-base md:text-sm"
          : "py-2.5 md:py-3 rounded-xl md:rounded-2xl text-base md:text-sm",
        // Padding horizontal según decoraciones
        Icon ? (sm ? "pl-9" : "pl-10") : sm ? "px-3" : "px-4",
        onClear && "pr-12",
        className,
      )}
      {...props}
    />
  );

  // Bare sin decoraciones → solo el input (hijo flex correcto / wrapper propio).
  if (bare && !decorated) return inputEl;

  return (
    <div className="relative w-full group/input-container">
      {/* Icono de la Izquierda */}
      {Icon && (
        <div
          className={cn(
            "absolute top-1/2 -translate-y-1/2 text-subtle group-focus-within/input-container:text-primary transition-colors duration-300 pointer-events-none",
            sm ? "left-3" : "left-4",
          )}
        >
          <Icon size={sm ? 14 : 18} strokeWidth={2} />
        </div>
      )}

      {inputEl}

      {/* Botón de Borrar (Condicionado al foco del grupo) */}
      {rightAction ? (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 invisible group-focus-within/input-container:opacity-100 group-focus-within/input-container:visible transition-all duration-300">
          {rightAction}
        </div>
      ) : (
        onClear &&
        hasValue && (
          <button
            type="button"
            onClick={handleClear}
            aria-label={t("clear")}
            className={cn(
              "absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center rounded-xl transition-all duration-300",
              "text-primary hover:text-destructive hover:bg-destructive/10 dark:text-muted-foreground dark:hover:bg-destructive/20",
              "opacity-0 invisible group-focus-within/input-container:opacity-100 group-focus-within/input-container:visible",
              "animate-in fade-in zoom-in",
            )}
          >
            <X size={16} strokeWidth={3} />
          </button>
        )
      )}
    </div>
  );
}

// PasswordField is a pure control (like Input) so the surrounding
// FormItem/FormControl owns the id↔htmlFor wiring. Rendering its own
// FormItem here would mint a second id and detach the external FormLabel
// (axe: label-title-only). Always wrap it in <FormControl> at the call site.
function PasswordField({
  field,
  disabled,
  placeholder = "••••••••",
  className,
}: {
  field: React.ComponentProps<typeof Input>;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}) {
  const [show, setShow] = React.useState(false);
  const { t } = useTranslation();
  return (
    <Input
      type={show ? "text" : "password"}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      rightAction={
        <button
          type="button"
          aria-label={show ? t("hide") : t("show")}
          onClick={() => setShow((v) => !v)}
          className="h-8 w-8 flex items-center justify-center rounded-xl transition-all duration-300 text-muted-foreground hover:text-primary dark:hover:text-white hover:bg-primary/10"
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      }
      {...field}
    />
  );
}
export { Input, PasswordField };
